import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import { Card, FTBody, Page, Table } from '../Fuse';
import RegOptionsStore from '../../stores/RegOptionsStore';
import SnackStore from '../../stores/SnackStore';
import ConsoleStore from '../../stores/ConsoleStore';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import FuseAlert from '../FuseControls/FuseAlert';
import EmailRegStore from '../../stores/EmailRegStore';
import EmailDomainsModal from './EmailDomainsModal/EmailDomainsModal';
import IpRangesModal from './IpRangesModal/IpRangesModal';
import IpRegStore from '../../stores/IpRegStore';
import { ROLE_READ_ONLY } from '../../stores/Administrators/AdministratorModalStore';
import './GeneralOptions.css';

const block = b.lock('GeneralOptions');

class GeneralOptions extends Component {
    static propTypes = {
        regOptions: RegOptionsStore.PropType.isRequired,
        emailReg: EmailRegStore.PropType.isRequired,
        ipReg: IpRegStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
    };

    emailDomainTableColumns = [{
        name: 'emailDomain',
        title: 'Email Domain',
        render: (emailDomain) => {
            const { primaryDomain } = this.props.regOptions;
            return <FTBody>
                {emailDomain}
                {emailDomain === primaryDomain ? (
                    <span className={block('PrimaryAdminInfo')}>
                        (Primary Domain)
                        <FuseInfoTooltip>
                            The Primary Domain is associated with the email address of the Primary Admin.
                            Please contact support for any changes needed.
                        </FuseInfoTooltip>
                    </span>
                ) : null}
            </FTBody>;
        },
    }];

    ipRangesTableColumns = [{
        name: 'ipAddressSubnetwork',
        title: 'IP Address / Subnetwork',
        render: (ipRange) => <FTBody>{ipRange}</FTBody>,
    }];

    emailDomainTableActions = [{
        label: 'Edit',
        icon: 'edit',
        onPress: () => {
            const { emailReg } = this.props;
            emailReg.setFromObj(this.props.regOptions);
            emailReg.domainsModal.open();
        },
    }];

    ipRangesTableActions = [{
        label: 'Edit',
        icon: 'edit',
        onPress: () => {
            const { ipReg } = this.props;
            ipReg.setFromObj(this.props.regOptions);
            ipReg.modal.open();
        },
    }];

    componentWillMount() {
        this.props.regOptions.load()
            .catch((error) => error && this.props.snack.showError(
                `Failed to load registration options. ${error.message}`));
    }

    renderAssociatedEmailDomains = () => {
        const { regOptions, console } = this.props;
        const readOnly = console.authority === ROLE_READ_ONLY.value;

        return <Card noPadding>
            <Table
                cellClassName={block('EmailDomainTableCell')}
                header={<Fragment>
                    Associated Email&nbsp;
                    <div className={block('TitleWordWithInfo')}>
                        Domains
                        <FuseInfoTooltip>
                            These email domains will be used to lookup this account for registration and login
                            routing.
                        </FuseInfoTooltip>
                    </div>
                    <p className={block('TableSubtitle')}>
                        Used to validate email entries in policies and configuration
                    </p>
                </Fragment>}
                actions={readOnly ? null : this.emailDomainTableActions}
                pagination
                search={regOptions.domainSearch}
                searchPlaceholder={'Search by Email Domain'}
                columns={this.emailDomainTableColumns}
                items={regOptions.filteredDomains}
            />
            <EmailDomainsModal />
        </Card>;
    };

    renderIpRegistrationWarning = () => {
        const { console } = this.props;

        return <div className={block('IpRegistrationAlertContainer')}>
            <FuseAlert
                type={'warning'}
                title={`IP Registration can only be used with
                Anonymous Usage enabled on the Default Browsing Role`}
            >
                <div className={block('IpRegistrationAlertMessage')}>
                    {'You can modify it from the '}
                    <a href={'/console/policies/browsing_roles'} target={'_blank'} className={block('Link')}>
                        Browsing Roles page
                    </a>
                    {!console.isSuperAdmin ?
                        '. To change the IP addresses please create a Support ticket with your request.' : null}
                </div>
            </FuseAlert>
        </div>;
    };

    renderAssociatedIpRanges = () => {
        const { console, regOptions } = this.props;

        return <Card noPadding>
            <Table
                header={<Fragment>
                    Associated IP&nbsp;
                    <div className={block('TitleWordWithInfo')}>
                        Ranges
                        <FuseInfoTooltip>
                            These IP addresses will be used to automatically register a browser if there is a match.
                            All resulting browser traffic will be anonymized.
                        </FuseInfoTooltip>
                    </div>
                    <p className={block('TableSubtitle')}>
                        Used to anonymously register browser and assign the default Browsing Role
                    </p>
                </Fragment>}
                columns={this.ipRangesTableColumns}
                searchPlaceholder={'Search by IP Address'}
                actions={console.isSuperAdmin && regOptions.hashEmail ? this.ipRangesTableActions : null}
                pagination
                search={regOptions.ipRangesSearch}
                items={regOptions.filteredIpRanges}
            />
            <IpRangesModal />
        </Card>;
    };

    render() {
        const { console, regOptions } = this.props;

        return <Page title="Product Settings - General Options">
            {!regOptions.loadStatus.isLoading ? (
                <Fragment>
                    {this.renderAssociatedEmailDomains()}
                    {console.isConsoleAdmin ? (
                        <Fragment>
                            {!regOptions.hashEmail ? this.renderIpRegistrationWarning() : null}
                            {this.renderAssociatedIpRanges()}
                        </Fragment>
                    ) : null}
                </Fragment>
            ) : null}
        </Page>;
    }
}

export default inject('regOptions', 'emailReg', 'ipReg', 'snack', 'console')(observer(GeneralOptions));
